import { BackendServiceName } from 'v2/types'
import type {
  RAgentJudgesResponse,
  RAoSessionsResponse,
  RCalibrateMetricResponse,
  RCalibrationExample,
  RGenerateMetricsResponse,
  RImproveMetricResponse,
  RJudge,
  RJudgeDeleteResponse,
  RJudgeScope,
  RJudgeTestResponse,
  RMetricFailedRunsResponse,
  RMetricsAnalyticsResponse,
} from 'v2/types/api/agent-observability'
import { createService } from './create-service'

const api = createService('', true, BackendServiceName.agentObservability, 'v1')

export interface JudgeDraftPayload {
  display_name: string
  description: string
  scope: RJudgeScope
  enabled: boolean
}

export interface AgentJudgeMappingEntry {
  judge_id: string
  enabled: boolean
}

export interface MetricDraft {
  name: string
  description: string
  scope: RJudgeScope
}

export const agentObservabilityService = {
  // The builder route's :id is byte-for-byte the AO agent_id.
  getAgentJudges: (agentId: string) => api.get<RAgentJudgesResponse>(`/agents/${agentId}/judges`),
  // REPLACE semantics: `judges` must be the agent's complete desired set.
  setAgentJudges: (agentId: string, judges: AgentJudgeMappingEntry[]) =>
    api.put<RAgentJudgesResponse>(`/agents/${agentId}/judges`, { judges }),
  createJudge: (payload: JudgeDraftPayload) => api.post<RJudge>('/judges', payload),
  updateJudge: (id: string, patch: Partial<JudgeDraftPayload>) =>
    api.patch<RJudge>(`/judges/${id}`, patch),
  deleteJudge: (id: string) => api.delete<RJudgeDeleteResponse>(`/judges/${id}`),
  // Dry run: judges the sessions without persisting verdicts (1–10 ids).
  testJudge: (id: string, sessionIds: string[]) =>
    api.post<RJudgeTestResponse>(`/judges/${id}/test`, { session_ids: sessionIds }),
  // account_id is injected server-side; never send it.
  getRecentSessions: (agentId: string, limit = 10, offset = 0) =>
    api.get<RAoSessionsResponse>('/sessions', { agent_id: agentId, limit, offset }),
  getMetricsAnalytics: (params: { agent_id?: string; range: string; target?: number }) =>
    api.get<RMetricsAnalyticsResponse>('/analytics/metrics', params),
  // ── AI authoring (account-blind AO routes; AO summarizes the flow server-side) ──
  improveDescription: (draft: MetricDraft) =>
    api.post<RImproveMetricResponse>('/judges/improve-description', draft),
  generateMetrics: (flow: unknown, existing: MetricDraft[]) =>
    api.post<RGenerateMetricsResponse>('/judges/generate', { flow, existing }),
  calibrateMetric: (payload: {
    description: string
    scope: RJudgeScope
    examples: RCalibrationExample[]
  }) => api.post<RCalibrateMetricResponse>('/judges/calibrate', payload),
  getMetricFailedRuns: (params: { metric: string; agent_id?: string; range: string }) =>
    api.get<RMetricFailedRunsResponse>('/analytics/metric-failed-runs', params),
}
